import { Button, Stack, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { MaterialComplianceDocument } from '@texas/api/endpoints/metadata/materialComplianceDocumentsApi';
import {
  complianceDocumentKindLabel,
  complianceDocumentTypeLabel,
} from '@texas/components/configuration/complianceDocumentDefinitions/translations';
import { EntityDirectoryFileThumbnail } from '@texas/components/shared/entityDirectory/EntityDirectoryFileThumbnail';

const columnHelper = createColumnHelper<MaterialComplianceDocument>();

export function materialComplianceDocumentColumns(
  t: TFunction,
  canEdit: boolean,
  onRemove: (document: MaterialComplianceDocument) => void,
) {
  return [
    columnHelper.accessor('logo', {
      header: t('complianceDocumentDefinitions.logo'),
      size: 80,
      minSize: 70,
      cell: (info) =>
        info.getValue() && (
          <EntityDirectoryFileThumbnail
            file={info.getValue()}
            alt={info.row.original.name}
          />
        ),
    }),
    columnHelper.accessor('name', {
      header: t('general.name'),
      size: 320,
      minSize: 240,
      cell: (info) => (
        <Stack spacing={1}>
          <Text variant="configuration-strong">{info.getValue()}</Text>
          <Text variant="entity-directory-description">
            {info.row.original.description}
          </Text>
          {info.row.original.archived && (
            <Text variant="entity-directory-status-inactive">
              {t('general.archived')}
            </Text>
          )}
        </Stack>
      ),
    }),
    columnHelper.accessor('kind', {
      header: t('complianceDocumentDefinitions.kindLabel'),
      size: 120,
      minSize: 120,
      cell: (info) => complianceDocumentKindLabel(info.getValue(), t),
    }),
    columnHelper.accessor('type', {
      header: t('complianceDocumentDefinitions.typeLabel'),
      size: 140,
      minSize: 140,
      cell: (info) => complianceDocumentTypeLabel(info.getValue(), t),
    }),
    columnHelper.accessor('added', {
      header: t('customerComplianceDocuments.dateAdded'),
      size: 140,
      minSize: 140,
      cell: (info) => dayjs(info.getValue()).locale('en').format('DD MMM YYYY'),
    }),
    ...(canEdit
      ? [
          columnHelper.display({
            id: 'actions',
            header: t('general.actions'),
            size: 110,
            minSize: 110,
            cell: (info) => (
              <Button
                size="sm"
                variant="entityDirectorySecondary"
                onClick={() => onRemove(info.row.original)}
              >
                {t('general.remove')}
              </Button>
            ),
          }),
        ]
      : []),
  ];
}
